
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, Outlet } from 'react-router-dom';
import Navbar from './components/Navbar.tsx';
import Footer from './components/Footer.tsx';
import Home from './pages/Home.tsx';
import BotoxAntwerpen from './pages/BotoxAntwerpen.tsx';
import BotoxEdegem from './pages/BotoxEdegem.tsx';
import BotoxHoboken from './pages/BotoxHoboken.tsx';
import FillersAntwerpen from './pages/FillersAntwerpen.tsx';
import SEOPage from './pages/SEOPage.tsx';
import AdminDashboard from './pages/AdminDashboard.tsx';
import LoginPage from './pages/LoginPage.tsx';
import TermsAndConditions from './pages/TermsAndConditions.tsx';
import PrivacyPolicy from './pages/PrivacyPolicy.tsx';
import CookiePolicy from './pages/CookiePolicy.tsx';
import Pricing from './pages/Pricing.tsx';
import { apiService } from './services/apiService.ts';
import { SiteSettings } from './types.ts';

const ProtectedRoute: React.FC<{ 
  isAuthenticated: boolean, 
  children: React.ReactElement 
}> = ({ isAuthenticated, children }) => {
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const PublicLayout: React.FC<{ settings: SiteSettings }> = ({ settings }) => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar settings={settings} />
      <main className="flex-grow">
        <Outlet />
      </main>
      <Footer settings={settings} />
    </div>
  );
};

const AppContent: React.FC<{ 
  data: any,
  isAuthenticated: boolean,
  onUpdate: () => void,
  onLogin: () => void
}> = ({ data, isAuthenticated, onUpdate, onLogin }) => {
  
  useEffect(() => {
    if (data) {
      setTimeout(() => {
        document.dispatchEvent(new Event('render-event'));
      }, 1000);
    }
  }, [data]);

  if (!data) {
    return (
       <div className="flex flex-col min-h-screen items-center justify-center bg-[#0a0a0a]">
         <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
       </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-theme-bg text-theme-text transition-colors duration-500">
      <Routes>
        <Route path="/login" element={<LoginPage onLogin={onLogin} />} />
        
        <Route path="/admin/*" element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <AdminDashboard 
              treatments={data.treatments} 
              locations={data.locations} 
              settings={data.settings} 
              categories={data.categories} 
              onUpdate={onUpdate} 
            />
          </ProtectedRoute>
        } />
        
        <Route element={<PublicLayout settings={data.settings} />}>
          <Route path="/" element={<Home treatments={data.treatments} settings={data.settings} />} />
          
          {/* SEO Priority Routes - LOCKED TO 10 */}
          <Route path="/botox-antwerpen" element={<BotoxAntwerpen settings={data.settings} />} />
          <Route path="/botox-edegem" element={<BotoxEdegem settings={data.settings} />} />
          <Route path="/botox-hoboken" element={<BotoxHoboken settings={data.settings} />} />
          <Route path="/fillers-antwerpen" element={<FillersAntwerpen settings={data.settings} />} />
          <Route path="/tarieven" element={<Pricing treatments={data.treatments} settings={data.settings} categories={data.categories} />} />
          
          <Route path="/lippen-opvullen-antwerpen" element={
            <SEOPage 
              settings={data.settings}
              title="Lip Fillers Antwerpen | Natuurlijk Lippen Opvullen bij Dr. An De Cuyper"
              h1="Lip fillers in Antwerpen voor natuurlijke lipcontour en subtiel volume"
              description="Lip fillers in Antwerpen voor natuurlijke lipcontour, hydratatie en subtiel volume. Medische aanpak door Dr. An De Cuyper met persoonlijk advies."
              canonical="https://andecuyper.be/lippen-opvullen-antwerpen"
              schema={{
                "@context": "https://schema.org",
                "@graph": [
                  {
                    "@type": "BreadcrumbList",
                    "@id": "https://andecuyper.be/lippen-opvullen-antwerpen#breadcrumb",
                    "itemListElement": [
                      {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://andecuyper.be"
                      },
                      {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "Lippen Opvullen Antwerpen",
                        "item": "https://andecuyper.be/lippen-opvullen-antwerpen"
                      }
                    ]
                  },
                  {
                    "@type": "FAQPage",
                    "@id": "https://andecuyper.be/lippen-opvullen-antwerpen#faq",
                    "mainEntity": [
                      {
                        "@type": "Question",
                        "name": "Wat is het verschil tussen lip fillers en lippen opvullen?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Er is materieel geen verschil; 'lip fillers' verwijst naar het gebruikte product (hyaluronzuur gel), terwijl 'lippen opvullen' de omschrijving is van de behandeling zelf. Beide termen worden vaak door elkaar gebruikt voor cosmetische lipcorrecties."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Zien lip fillers er altijd opvallend uit?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Zeker niet. De mate van opvallendheid hangt volledig af van de hoeveelheid ingespoten filler en de gebruikte techniek. Dr. An De Cuyper streeft altijd naar een uiterst subtiel, harmonieus en natuurlijk resultaat dat perfect past bij uw gezichtsverhoudingen."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Hoe lang blijft het resultaat van lip fillers zichtbaar?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Het resultaat van een behandeling met hyaluronzuur lip fillers blijft gemiddeld 6 tot 12 maanden zichtbaar. De exacte duur verschilt per individu en is onder andere afhankelijk van uw metabolisme, levensstijl en de dichtheid van het geplaatste product."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Is zwelling normaal na lip fillers?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Ja, de lippen zijn een zeer gevoelig gebied en tijdelijke zwelling na de behandeling is volkomen normaal. Deze zwelling is meestal het meest uitgesproken in de eerste 24 tot 48 uur en neemt daarna geleidelijk af."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Kan ik eerst advies krijgen zonder meteen te behandelen?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Absoluut. Wij geloven in bewuste en weloverwogen keuzes. Tijdens een vrijblijvende eerste consultatie bespreekt Dr. An De Cuyper uw wensen, analyseert zij de anatomie van uw lippen, en stelt zij een persoonlijk behandelplan op zonder enige verplichting tot directe behandeling."
                        }
                      }
                    ]
                  }
                ]
              }}
              content={
                <div className="space-y-12 text-theme-muted font-light leading-relaxed">
                  <section className="space-y-4">
                    <p className="text-2xl text-theme-text font-medium leading-normal italic border-l-4 border-primary pl-8">
                      Lip fillers in Antwerpen bij onze kliniek onder medische leiding van Dr. An De Cuyper is een gespecialiseerde medisch-esthetische behandeling gericht op het herstellen of verfijnen van de natuurlijke lipcontour, hydratatie, subtiel volume en de algehele gezichtsbalans. Onze focus ligt altijd op subtiele en harmonieuze esthetiek die past bij uw unieke gelaat.
                    </p>
                    <p>
                      Of u nu droomt van meer volume, uw lipcontour wilt verduidelijken of een lichte asymmetrie wilt corrigeren, wij zorgen voor een veilige en professionele behandeling met uitsluitend hoogwaardige tijdelijke hyaluronzuur lip fillers.
                    </p>
                    <div className="pt-4">
                      <a 
                        href={data.settings.bookingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-primary text-black px-8 py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white transition-all inline-block"
                      >
                        Plan een persoonlijk advies
                      </a>
                    </div>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Voor wie zijn lip fillers geschikt?</h2>
                    <p>
                      Lippen zijn een zeer bepalend kenmerk van uw gezicht. Lip fillers zijn geen universele oplossing, maar kunnen wel een gerichte ondersteuning bieden bij verschillende indicaties. Een behandeling kan geschikt zijn bij:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-theme-muted">
                      <li><strong>Dunne lippen:</strong> voor een subtiele en natuurlijke toename van volume.</li>
                      <li><strong>Verlies van volume:</strong> het herstellen van de volheid die door natuurlijke veroudering is afgenomen.</li>
                      <li><strong>Minder duidelijke lipcontour:</strong> het herdefiniëren van de liprand en de Cupido-boog.</li>
                      <li><strong>Asymmetrie:</strong> het corrigeren van ongelijkheden tussen de linker- en rechterzijde of de boven- en onderlip.</li>
                      <li><strong>Droge of minder gehydrateerde lippen:</strong> hyaluronzuur lip fillers trekken vocht aan en kunnen de lippen een gezondere, gladdere en intens gehydrateerde uitstraling geven.</li>
                      <li><strong>Wens voor een subtieler of frisser resultaat:</strong> voor een frisse uitstraling zonder ingrijpende veranderingen.</li>
                    </ul>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Natuurlijk resultaat: geen overdreven lippen</h2>
                    <p>
                      Dr. An De Cuyper hanteert een strikte esthetische filosofie waarbij natuurlijke harmonie centraal staat. De bekende 'duckface' of overdreven lippen worden te allen tijde vermeden. Door conservatieve doseringen toe te passen en de natuurlijke anatomie van uw mond te respecteren, blijft uw eigen karakter behouden.
                    </p>
                    <p>
                      Afhankelijk van de anatomie is een geleidelijke opbouw vaak de beste weg. Niet elke patiënt heeft een toename in volume nodig; soms is het verfijnen van de contouren of het herstellen van de hydratatie al voldoende om een prachtig, fris resultaat te behalen.
                    </p>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Hydratatie, contour, volume en asymmetrie</h2>
                    <p>
                      Bij hyaluronzuur lip fillers stemmen we de techniek en de hoeveelheid specifiek af op uw wensen:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-theme-muted">
                      <li><strong>Hydratatie:</strong> Door een zeer vloeibare filler te gebruiken, focussen we op het herstellen van de vochtbalans. Dit geeft een subtiele glans en vermindert fijne droogtelijntjes zonder extra volume toe te voegen.</li>
                      <li><strong>Contour definitie:</strong> We accentueren de randen van de lippen (de Cupidoboog en de filterlijntjes) om de natuurlijke vorm elegant te accentueren.</li>
                      <li><strong>Subtiel volume:</strong> We plaatsen de hyaluronzuur filler strategisch in het lippenrood om een zacht en natuurlijk volumeherstel te realiseren dat in perfecte balans is met uw neus, kin en jukbeenderen.</li>
                      <li><strong>Asymmetrie corrigeren:</strong> Met uiterste precisie kunnen we kleine verschillen in vorm en volume corrigeren om een mooi en symmetrisch resultaat te ondersteunen.</li>
                    </ul>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Het verloop van de behandeling</h2>
                    <p>
                      Elke behandeling bij MD An De Cuyper verloopt volgens een gestructureerd medisch proces:
                    </p>
                    <ol className="list-decimal pl-6 space-y-2 text-theme-muted">
                      <li><strong>Consultatie & Analyse:</strong> We starten met een uitgebreid gesprek en een grondige medische analyse van uw lippen en gezichtsbalans om uw wensen zorgvuldig in kaart te brengen.</li>
                      <li><strong>Behandelplan:</strong> We bespreken de mogelijkheden en bepalen de meest geschikte aanpak en de te gebruiken hoeveelheid product op basis van uw anatomie.</li>
                      <li><strong>De Behandeling:</strong> De injectiebehandeling wordt met uiterste precisie uitgevoerd door Dr. An De Cuyper. Er kan een milde verdovende crème worden aangebracht, en de filler zelf bevat meestal ook een verdovingsmiddel om het comfort te verhogen.</li>
                      <li><strong>Nazorgadvies:</strong> Na de behandeling ontvangt u gericht advies over de nazorg om het herstel zo soepel mogelijk te laten verlopen.</li>
                    </ol>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Nazorg na lip fillers</h2>
                    <p>
                      Na een behandeling met lip fillers is het belangrijk om de adviezen van de kliniek nauwgezet op te volgen. Er kan tijdelijke zwelling, roodheid, gevoeligheid of blauwe plekken optreden na de behandeling. Dit is een volstrekt normale reactie en trekt meestal binnen enkele dagen weg.
                    </p>
                    <p>
                      Vermijd gedurende de eerste 24 uur zware fysieke inspanningen, alcohol, direct zonlicht en de zonnebank. Oefen geen onnodige druk of massage uit op de lippen, tenzij dit expliciet door de arts is geadviseerd. Mocht u ongebruikelijke of onverwachte symptomen ervaren, neem dan direct contact op met onze kliniek.
                    </p>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Lip fillers versus andere fillers</h2>
                    <p>
                      Lip fillers zijn een specifieke en delicate vorm van dermale fillers. De lippen zijn intensief doorbloed en hebben een zeer beweeglijke anatomie, waardoor deze behandeling uiterste precisie en terughoudendheid vraagt van de behandelend arts. Voor andere zones van het gelaat zoals de jukbeenderen, kin of kaaklijn verwijzen we u graag naar onze algemene pagina over <a href="/fillers-antwerpen" className="text-primary underline hover:text-white transition-colors font-medium">Fillers Antwerpen</a>. Al onze behandelingen en bijbehorende kosten kunt u terugvinden op onze pagina met <a href="/tarieven" className="text-primary underline hover:text-white transition-colors font-medium">tarieven</a>. Verder bieden wij ook <a href="/botox-antwerpen" className="text-primary underline hover:text-white transition-colors font-medium">Botox Antwerpen</a> aan voor dynamische rimpels.
                    </p>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold pt-4">Lip fillers in Antwerpen, Edegem en Hoboken</h2>
                    <p>
                      Of u nu langskomt op onze hoofdlocatie Clinic 2650 of in onze vestigingen in Edegem, Hoboken of Burcht, u geniet overal van dezelfde hoogwaardige medisch-esthetische zorg onder leiding van Dr. An De Cuyper. Onze praktijken zijn vlot bereikbaar voor patiënten uit de hele Antwerpse regio.
                    </p>
                  </section>

                  <section className="space-y-6 pt-8 not-prose">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Veelgestelde Vragen over Lip Fillers</h2>
                    <div className="space-y-4">
                      <details className="bg-theme-surface p-6 rounded-2xl border border-theme-border cursor-pointer group">
                        <summary className="font-bold text-theme-text list-none flex justify-between items-center">
                          <span>Wat is het verschil tussen lip fillers en lippen opvullen?</span>
                          <span className="text-primary transition-transform group-open:rotate-180">&darr;</span>
                        </summary>
                        <p className="mt-4 text-theme-muted font-light text-sm">
                          Er is materieel geen verschil; 'lip fillers' verwijst naar het gebruikte product (hyaluronzuur gel), terwijl 'lippen opvullen' de omschrijving is van de behandeling zelf. Beide termen worden vaak door elkaar gebruikt voor cosmetische lipcorrecties.
                        </p>
                      </details>
                      <details className="bg-theme-surface p-6 rounded-2xl border border-theme-border cursor-pointer group">
                        <summary className="font-bold text-theme-text list-none flex justify-between items-center">
                          <span>Zien lip fillers er altijd opvallend uit?</span>
                          <span className="text-primary transition-transform group-open:rotate-180">&darr;</span>
                        </summary>
                        <p className="mt-4 text-theme-muted font-light text-sm">
                          Zeker niet. De mate van opvallendheid hangt volledig af van de hoeveelheid ingespoten filler en de gebruikte techniek. Dr. An De Cuyper streeft altijd naar een uiterst subtiel, harmonieus en natuurlijk resultaat dat perfect past bij uw gezichtsverhoudingen.
                        </p>
                      </details>
                      <details className="bg-theme-surface p-6 rounded-2xl border border-theme-border cursor-pointer group">
                        <summary className="font-bold text-theme-text list-none flex justify-between items-center">
                          <span>Hoe lang blijft het resultaat van lip fillers zichtbaar?</span>
                          <span className="text-primary transition-transform group-open:rotate-180">&darr;</span>
                        </summary>
                        <p className="mt-4 text-theme-muted font-light text-sm">
                          Het resultaat van een behandeling met hyaluronzuur lip fillers blijft gemiddeld 6 tot 12 maanden zichtbaar. De exacte duur verschilt per individu en is onder andere afhankelijk van uw metabolisme, levensstijl en de dichtheid van het geplaatste product.
                        </p>
                      </details>
                      <details className="bg-theme-surface p-6 rounded-2xl border border-theme-border cursor-pointer group">
                        <summary className="font-bold text-theme-text list-none flex justify-between items-center">
                          <span>Is zwelling normaal na lip fillers?</span>
                          <span className="text-primary transition-transform group-open:rotate-180">&darr;</span>
                        </summary>
                        <p className="mt-4 text-theme-muted font-light text-sm">
                          Ja, de lippen zijn een zeer gevoelig gebied en tijdelijke zwelling na de behandeling is volkomen normaal. Deze zwelling is meestal het meest uitgesproken in de eerste 24 tot 48 uur en neemt daarna geleidelijk af.
                        </p>
                      </details>
                      <details className="bg-theme-surface p-6 rounded-2xl border border-theme-border cursor-pointer group">
                        <summary className="font-bold text-theme-text list-none flex justify-between items-center">
                          <span>Kan ik eerst advies krijgen zonder meteen te behandelen?</span>
                          <span className="text-primary transition-transform group-open:rotate-180">&darr;</span>
                        </summary>
                        <p className="mt-4 text-theme-muted font-light text-sm">
                          Absoluut. Wij geloven in bewuste en weloverwogen keuzes. Tijdens een vrijblijvende eerste consultatie bespreekt Dr. An De Cuyper uw wensen, analyseert zij de anatomie van uw lippen, en stelt zij een persoonlijk behandelplan op zonder enige verplichting tot directe behandeling.
                        </p>
                      </details>
                    </div>
                  </section>
                </div>
              }
            />
          } />

          <Route path="/rimpelbehandeling-antwerpen" element={
            <SEOPage 
              settings={data.settings}
              title="Rimpelbehandeling Antwerpen | Verjonging & Advies | Dr. An De Cuyper"
              h1="Rimpelbehandeling Antwerpen"
              description="Effectieve rimpelbehandeling in Antwerpen door medisch expert Dr. An De Cuyper. Botox, fillers en huidverbetering voor een jeugdige blik. Plan uw afspraak."
              canonical="https://andecuyper.be/rimpelbehandeling-antwerpen"
              content={
                <>
                  <p>Rimpels zijn een natuurlijk verschijnsel, maar ze kunnen u een uitstraling geven die niet matcht met hoe u zich voelt. Inregio Antwerpen bieden we een breed scala aan medische rimpelbehandelingen.</p>
                  <p>Van spierontspanners (Botox) voor dynamische lijnen tot fillers voor diepere plooien en draadjesliften voor verslapping - we creëren een gepersonaliseerd plan voor uw verjonging.</p>
                </>
              }
            />
          } />

          <Route path="/profhilo-antwerpen" element={
            <SEOPage 
              settings={data.settings}
              title="Profhilo Antwerpen | Bioremodeling & Huidverstrakking | MD De Cuyper"
              h1="Profhilo Antwerpen: Huidverstrakking"
              description="Ontdek Profhilo in Antwerpen bij Dr. An De Cuyper. Krachtige bioremodeling voor huidverstrakking zonder volume-effect. Voor gezicht, hals en handen."
              canonical="https://andecuyper.be/profhilo-antwerpen"
              content={
                <>
                  <p>Profhilo is geen filler, maar een bioremodellerend product. Het bevat een van de hoogste concentraties puur hyaluronzuur op de markt. In onze praktijk in Antwerpen gebruiken we dit om de huid van binnenuit te stimuleren.</p>
                  <p>Het resultaat? Een stevigere, glanzende huid met minder fijne lijntjes, zonder dat uw gezichtsvorm verandert. Ideaal voor wie een natuurlijke huidverbetering zoekt.</p>
                </>
              }
            />
          } />

          <Route path="/huidverbetering-antwerpen" element={
            <SEOPage 
              settings={data.settings}
              title="Huidverbetering Antwerpen | Huidkliniek Dr. An De Cuyper"
              h1="Huidverbetering in Antwerpen met een medische en persoonlijke aanpak"
              description="Huidverbetering in Antwerpen voor een frissere, egalere en gezondere huid. Persoonlijk huidadvies en medische aanpak door Dr. An De Cuyper."
              canonical="https://andecuyper.be/huidverbetering-antwerpen"
              schema={{
                "@context": "https://schema.org",
                "@graph": [
                  {
                    "@type": "BreadcrumbList",
                    "@id": "https://andecuyper.be/huidverbetering-antwerpen#breadcrumb",
                    "itemListElement": [
                      {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://andecuyper.be/"
                      },
                      {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "Huidverbetering Antwerpen",
                        "item": "https://andecuyper.be/huidverbetering-antwerpen"
                      }
                    ]
                  },
                  {
                    "@type": "FAQPage",
                    "@id": "https://andecuyper.be/huidverbetering-antwerpen#faq",
                    "mainEntity": [
                      {
                        "@type": "Question",
                        "name": "Welke behandeling past het best bij mijn huid?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Dit is afhankelijk van uw huidconditie, wensen en behoeften. Tijdens een persoonlijk consult voert Dr. An De Cuyper een grondige huidanalyse uit om een passend en realistisch behandelplan op te stellen."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Is huidverbetering hetzelfde als huidverjonging?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Hoewel ze elkaar overlappen, richt huidverbetering zich primair op de gezondheid, textuur en barrièrefunctie van de huid (bijvoorbeeld hydratatie, pigment- of poriënstructuur). Huidverjonging focust daarnaast ook op het verzachten van rimpels of het aanpakken van volumeverlies en huidverslapping."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Hoeveel behandelingen zijn meestal nodig?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Dit varieert sterk per persoon en per gekozen behandeling. Sommige gelaatsbehandelingen zoals HydraFacial geven een direct resultaat na één sessie, terwijl medische peelings of laserbehandelingen vaak in kuurvorm worden ingezet voor een optimaal en langdurig effect."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Kan huidverbetering gecombineerd worden met injectables?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Ja, behandelingen voor huidkwaliteit zijn een uitstekende basis die perfect gecombineerd kan worden met Botox of fillers voor een gelaatsharmonie. Dit wordt uitvoerig besproken tijdens uw adviesgesprek."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Kan ik eerst een huidanalyse laten uitvoeren?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Absoluut. Elke succesvolle behandeling start met een nauwkeurige intake en analyse om uw doelen in kaart te brengen en de geschiktheid van behandelopties te beoordelen."
                        }
                      }
                    ]
                  }
                ]
              }}
              content={
                <div className="space-y-12 text-theme-muted">
                  <section className="space-y-4">
                    <p>Medische huidverbetering bij Clinic 2650 onder leiding van Dr. An De Cuyper is een allesomvattende, wetenschappelijk onderbouwde methodologie om uw huidkwaliteit stapsgewijs te verbeteren, te ondersteunen en beter in balans te brengen. In plaats van symptomatische verhulling, focussen we ons op een structurele aanpak van binnenuit.</p>
                    <p>Of u nu in onze centraal gelegen praktijk in Antwerpen langskomt of kiest voor onze moderne vestigingen, u kunt rekenen op een persoonlijke benadering met resultaatgerichte, klinisch geteste behandelmethoden.</p>
                    
                    <div className="pt-4">
                      <a 
                        href="/contact" 
                        className="inline-flex items-center text-primary underline font-bold hover:text-white transition-all"
                      >
                        Plan een persoonlijk huidadvies &rarr;
                      </a>
                    </div>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Welke huidproblemen kunnen we verbeteren?</h2>
                    <p>Met de juiste behandelingsopzet kunnen we uiteenlopende indicaties gericht en zorgvuldig begeleiden. De medische benadering richt zich onder andere op:</p>
                    <ul className="list-disc pl-6 space-y-2 text-theme-muted">
                      <li><strong>Doffe huid of vermoeide uitstraling:</strong> Ondersteunen van een frissere, natuurlijkere uitstraling van de huid.</li>
                      <li><strong>Fijne lijntjes:</strong> Verzachten van oppervlakkige kraaienpootjes en droogtelijntjes.</li>
                      <li><strong>Pigmentvlekken:</strong> Egaliseren van zonschade en milde ouderdomsvlekjes.</li>
                      <li><strong>Roodheid & gevoelige vaten:</strong> Kalmeren van de overgevoelige huid.</li>
                      <li><strong>Ongelijkmatige huidtextuur:</strong> Verhogen van de elasticiteit en soepelheid.</li>
                      <li><strong>Grove poriën & onzuiverheden:</strong> Verfijnen van het huidoppervlak en de talgregulatie.</li>
                      <li><strong>Beginnende huidverslapping:</strong> Stimuleren van collageen- en elastineproductie.</li>
                      <li><strong>Acnegevoelige huid:</strong> Behoedzame kalmering van onrustige zones en vermindering van verstoppingen.</li>
                    </ul>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Van huidanalyse naar behandelplan</h2>
                    <p>Elk gelaat is uniek. Daarom gelooft Dr. An De Cuyper niet in standaardoplossingen, maar in een medisch onderbouwd stappenplan:</p>
                    <ol className="list-decimal pl-6 space-y-2 text-theme-muted">
                      <li><strong>Persoonlijke intake:</strong> We bespreken uw levensstijl, uw huidige huidverzorgingsroutine, medische geschiedenis en prioriteiten.</li>
                      <li><strong>Grondige huidanalyse:</strong> We beoordelen de dikte, hydratatie, pigmentatie en elasticiteit van de huidbarrière.</li>
                      <li><strong>Verwachtingsmanagement:</strong> We bespreken open en eerlijk wat medisch haalbaar is om teleurstellingen te voorkomen.</li>
                      <li><strong>Keuze van behandeling:</strong> Selectie van de meest geschikte klinische behandelingen op basis van de analyse.</li>
                      <li><strong>Realistisch behandelplan:</strong> Een transparant plan met kuren of onderhoudsbehandelingen op maat.</li>
                      <li><strong>Opvolging:</strong> We evalueren de voortgang en sturen het plan bij waar nodig voor optimale gelaatsharmonie.</li>
                    </ol>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Mogelijke behandelingen binnen huidverbetering</h2>
                    <p>In onze professionele huidkliniek Antwerpen maken we gebruik van beproefde methoden om uw huidbarrière te versterken en gelaatsverjonging te bereiken:</p>
                    <ul className="list-disc pl-6 space-y-2 text-theme-muted">
                      <li><a href="/hydrafacial-antwerpen" className="text-primary underline hover:text-white transition-colors font-medium">HydraFacial Antwerpen</a>: Onze ultieme 3-staps dieptereiniging voor een frisse glow en het intensief reinigen van verstopte poriën.</li>
                      <li><a href="/profhilo-antwerpen" className="text-primary underline hover:text-white transition-colors font-medium">Profhilo Antwerpen</a>: Revolutionaire bioremodeling op basis van puur hyaluronzuur om slappe huid van binnenuit te verstrakken zonder extra volume.</li>
                      <li><a href="/laserbehandeling-antwerpen" className="text-primary underline hover:text-white transition-colors font-medium">Laserbehandeling Antwerpen</a>: Fractionele CO2 laser resurfacing kan, afhankelijk van de indicatie, worden ingezet voor een diepere vernieuwing van de huidstructuur bij rimpels en littekens.</li>
                      <li><a href="/tarieven" className="text-primary underline hover:text-white transition-colors font-medium">Tarieven</a>: Bekijk onze transparante prijzenpagina voor alle behandelingen.</li>
                    </ul>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Waarom medische huidverbetering?</h2>
                    <p>Een behandeling onder toezicht van een arts biedt belangrijke voordelen op het gebied van veiligheid, precisie en resultaatgerichtheid. Dankzij onze medische achtergrond kunnen we de huid intensiever en gerichter behandelen, met gebruik van streng gecontroleerde, veilige stoffen en medische apparatuur. Dit helpt risico's beter inschatten en beperken, en ondersteunt een doordachte aanpak van de huidkwaliteit.</p>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Huidverbetering in Antwerpen, Edegem en Hoboken</h2>
                    <p>Onze praktijk is uitstekend bereikbaar voor cliënten uit heel de regio Antwerpen, waaronder Edegem (Clinic 2650), Hoboken, Wilrijk en Burcht. Of u nu kiest voor preventieve zorg of het herstellen van uw natuurlijke huidtextuur, wij bieden kwalitatieve medische ondersteuning dicht bij u in de buurt.</p>
                  </section>

                  <section className="space-y-6 pt-6 border-t border-white/10">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Veelgestelde Vragen (FAQ)</h2>
                    <div className="space-y-6">
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Welke behandeling past het best bij mijn huid?</h4>
                        <p className="text-theme-muted">Dit is afhankelijk van uw huidconditie, wensen en behoeften. Tijdens een persoonlijk consult voert Dr. An De Cuyper een grondige huidanalyse uit om een passend en realistisch behandelplan op te stellen.</p>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Is huidverbetering hetzelfde als huidverjonging?</h4>
                        <p className="text-theme-muted">Hoewel ze elkaar overlappen, richt huidverbetering zich primair op de gezondheid, textuur en barrièrefunctie van de huid (bijvoorbeeld hydratatie, pigment- of poriënstructuur). Huidverjonging focust daarnaast ook op het verzachten van rimpels of het aanpakken van volumeverlies en huidverslapping.</p>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Hoeveel behandelingen zijn meestal nodig?</h4>
                        <p className="text-theme-muted">Dit varieert sterk per persoon en per gekozen behandeling. Sommige gelaatsbehandelingen zoals HydraFacial geven een direct resultaat na één sessie, terwijl medische peelings of laserbehandelingen vaak in kuurvorm worden ingezet voor een optimaal en langdurig effect.</p>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Kan huidverbetering gecombineerd worden met injectables?</h4>
                        <p className="text-theme-muted">Ja, behandelingen voor huidkwaliteit zijn een uitstekende basis die perfect gecombineerd kan worden met Botox of fillers voor een gelaatsharmonie. Dit wordt uitvoerig besproken tijdens uw adviesgesprek.</p>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Kan ik eerst een huidanalyse laten uitvoeren?</h4>
                        <p className="text-theme-muted">Absoluut. Elke succesvolle behandeling start met een nauwkeurige intake en analyse om uw doelen in kaart te brengen en de geschiktheid van behandelopties te beoordelen.</p>
                      </div>
                    </div>
                  </section>
                </div>
              }
            />
          } />

          <Route path="/draadjeslift-antwerpen" element={
            <SEOPage 
              settings={data.settings}
              title="Draadjeslift Antwerpen | APTOS Lifting | Dr. An De Cuyper"
              h1="Draadjeslift Antwerpen (APTOS)"
              description="Non-chirurgische facelift met draadjeslift in Antwerpen door expert Dr. An De Cuyper. Lift uw kaaklijn en wangen zonder operatie. Boek een consult."
              canonical="https://andecuyper.be/draadjeslift-antwerpen"
              content={
                <>
                  <p>De draadjeslift (vaak met APTOS draden) is een revolutionaire techniek om verslapping in het gezicht en de hals tegen te gaan zonder operatie. Dr. De Cuyper is in de regio Antwerpen een van de weinige gecertificeerde artsen voor deze procedure.</p>
                  <p>De draden worden onderhuids geplaatst om weefsel te liften en de aanmaak van eigen collageen te stimuleren, wat zorgt voor een langdurig verjongend effect.</p>
                </>
              }
            />
          } />

          <Route path="/laserbehandeling-antwerpen" element={
            <SEOPage 
              settings={data.settings}
              title="Laserbehandeling Antwerpen | CO2 Laser Resurfacing | Dr. An De Cuyper"
              h1="Laserbehandeling Antwerpen"
              description="Medische CO2 laserbehandeling in Antwerpen voor rimpels en littekens door Dr. An De Cuyper. Herstel de structuur van uw huid met onze lasertechnologie."
              canonical="https://andecuyper.be/laserbehandeling-antwerpen"
              content={
                <>
                  <p>CO2 Lasers zijn de gouden standaard voor huidverjonging. In onze kliniek te Antwerpen gebruiken we lasertechnologie om de diepere huidlagen te vernieuwen.</p>
                  <p>Deze behandeling is ideaal voor het verminderen van diepe rimpels, acne-littekens en zonneschade, met een significante verbetering van de huidkwaliteit als resultaat.</p>
                </>
              }
            />
          } />

          <Route path="/hydrafacial-antwerpen" element={
            <SEOPage 
              settings={data.settings}
              title="HydraFacial Antwerpen | Dieptereiniging & Glow bij Dr. An De Cuyper"
              h1="HydraFacial in Antwerpen for diepe reiniging en huidverbetering"
              description="HydraFacial in Antwerpen voor diepe reiniging, hydratatie en een frisse glow zonder lange hersteltijd. Persoonlijk huidadvies bij Dr. An De Cuyper."
              canonical="https://andecuyper.be/hydrafacial-antwerpen"
              schema={{
                "@context": "https://schema.org",
                "@graph": [
                  {
                    "@id": "https://andecuyper.be/hydrafacial-antwerpen#breadcrumb",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                      {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://andecuyper.be/"
                      },
                      {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "HydraFacial Antwerpen",
                        "item": "https://andecuyper.be/hydrafacial-antwerpen"
                      }
                    ]
                  },
                  {
                    "@id": "https://andecuyper.be/hydrafacial-antwerpen#faq",
                    "@type": "FAQPage",
                    "mainEntity": [
                      {
                        "@type": "Question",
                        "name": "Is HydraFacial geschikt voor een gevoelige huid?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Ja, een HydraFacial-behandeling kan zo worden geconfigureerd dat deze ook geschikt is voor een gevoelige huid. De intensiteit en de gebruikte serums kunnen tijdens een persoonlijk consult worden afgestemd op uw specifieke barrièrefunctie."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Hoe snel zie ik resultaat na een HydraFacial?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Vaak is er direct na de behandeling al een subtiele verbetering in hydratatie en glans merkbaar. De huid ziet er doorgaans frisser en schoner uit."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Hoe vaak kan ik een HydraFacial laten uitvoeren?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Om de huidconditie optimaal te ondersteunen, kan een maandelijkse sessie worden geadviseerd. Dit bespreken we graag tijdens een persoonlijk adviesgesprek."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Is er hersteltijd na HydraFacial?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "Er is vrijwel geen sprake van een lange hersteltijd. Er kan een lichte, tijdelijke roodheid of milde gevoeligheid ontstaan, maar de meeste cliënten kunnen hun dagelijkse activiteiten direct hervatten."
                        }
                      },
                      {
                        "@type": "Question",
                        "name": "Wat is het verschil tussen HydraFacial en huidverbetering?",
                        "acceptedAnswer": {
                          "@type": "Answer",
                          "text": "HydraFacial is een specifieke 3-staps behandeling gericht op intensieve dieptereiniging, exfoliatie en hydratatie. Huidverbetering is een breder medisch-esthetisch concept waarbij verschillende therapieën (zoals peelings, skinboosters of lasers) in een behandelplan worden gecombineerd op basis van een diepgaande huidanalyse."
                        }
                      }
                    ]
                  }
                ]
              }}
              content={
                <div className="space-y-12 text-theme-muted">
                  <section className="space-y-4">
                    <p>De HydraFacial behandeling bij onze kliniek in Antwerpen onder medische leiding van Dr. An De Cuyper is een gepatenteerde, geavanceerde gelaatsverzorging die reiniging, exfoliatie, extractie, diepe hydratatie en antioxidant-bescherming combineert. De unieke Vortex-Fusion® technologie helpt om onzuiverheden en dode huidcellen behoedzaam te verwijderen en tegelijkertijd herstellende, specifieke serums in te sluizen.</p>
                    <p>Of u nu streeft naar een frisse glow voor een speciale gelegenheid of uw maandelijkse dieptereiniging in Antwerpen zoekt, HydraFacial stimuleert een gezonde uitstraling en verbetert uw algehele huidtextuur op een milde, ontspannende wijze.</p>
                    
                    <div className="pt-4">
                      <a 
                        href="/contact" 
                        className="inline-flex items-center text-primary underline font-bold hover:text-white transition-all"
                      >
                        Plan een persoonlijk huidadvies &rarr;
                      </a>
                    </div>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Voor wie is HydraFacial geschikt?</h2>
                    <p>HydraFacial is een veelzijdige behandeling die zo kan worden ingesteld dat deze aansluit bij verschillende huidtypen en specifieke wensen. HydraFacial kan geschikt zijn bij een doffe huid, verstopte poriën, grove poriën, onzuiverheden en een huid die extra hydratatie nodig heeft. De behandeling kan helpen bij het verminderen en verzachten van:</p>
                    <ul className="list-disc pl-6 space-y-2 text-theme-muted">
                      <li><strong>Doffe huid:</strong> Geeft vermoeide gezichtszones een levendige en egale glow facial Antwerpen uitstraling.</li>
                      <li><strong>Verstopte poriën:</strong> Reinigt talgophopingen en vermindert mee-eters en onzuiverheden op een milde manier.</li>
                      <li><strong>Grove poriën:</strong> Helpt de poriënstructuur optisch te verfijnen door grondig poriën reinigen Antwerpen.</li>
                      <li><strong>Onzuiverheden:</strong> Ondersteunt het zachtjes huid reinigen Antwerpen zonder de barrière hard aan te pakken.</li>
                      <li><strong>Droge of vochtarme huid:</strong> ondersteunt intensieve hydratatie van de bovenste huidlagen.</li>
                      <li><strong>Fijne lijntjes:</strong> Helpt milde rimpeltjes en droogtelijntjes optisch gladder te maken door krachtige hydratatie.</li>
                      <li><strong>Vermoeide huid:</strong> kan de huidtextuur verfijnen voor een uitgeruste blik.</li>
                      <li><strong>Ongelijkmatige huidtextuur:</strong> Maakt het oppervlak voelbaar zachter en egaler.</li>
                    </ul>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Wat maakt HydraFacial anders dan een klassieke gelaatsverzorging?</h2>
                    <p>In tegenstelling tot een klassieke gezichtsbehandeling of handmatige diepe reiniging, maakt HydraFacial gebruik van een gepatenteerde, medisch gecertificeerde vortex-technologie. De voordelen op een rij:</p>
                    <ul className="list-disc pl-6 space-y-2 text-theme-muted">
                      <li><strong>Gestructureerde stappen:</strong> Elke fase van de sessie is exact gekalibreerd.</li>
                      <li><strong>Synergie van technieken:</strong> Het combineert mechanische exfoliatie, een milde chemische peeling-infusie, vacuümextractie en actieve insluizing van serums in één vloeiende behandeling.</li>
                      <li><strong>Gerichte actieve stoffen:</strong> We werken met hoogwaardige peptides, hyaluronzuren en antioxidanten.</li>
                      <li><strong>Resultaat- en medisch georiënteerd:</strong> Het gaat verder dan louter ontspanning, zonder zuren die de huid beschadigen of de barrière forceren. Desalniettemin vervangt de verzorging geen specifieke dermatologische acne- of littekentherapie, maar vormt het een ideale aanvulling.</li>
                    </ul>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Het verloop van de behandeling</h2>
                    <p>In onze praktijk te Antwerpen verloopt de HydraFacial behandeling volgens een vastgesteld, medisch onderbouwd protocol over een gemiddelde duur van 30 tot 45 minuten:</p>
                    <ol className="list-decimal pl-6 space-y-2 text-theme-muted">
                      <li><strong>Huidanalyse / korte intake:</strong> We controleren de barrièrefunctie van uw gelaat om eventuele contra-indicaties te bespreken en de serums af te stemmen.</li>
                      <li><strong>Reinigen & Exfoliëren:</strong> Een grondige reiniging en milde peeling-oplossing (met glycolzuur en salicylzuur) weekt de dode huidcellen effectief los.</li>
                      <li><strong>Extractie van onzuiverheden:</strong> Met milde vacuümzuigkracht worden verstopte poriën, mee-eters en talgresten meestal comfortabel verwijderd.</li>
                      <li><strong>Hydratatie / serum-infusie:</strong> De gereinigde poriën worden intensief gevoed en verzadigd met een rijk serum van antioxidanten, peptides en hydraterend hyaluronzuur.</li>
                      <li><strong>Nazorgadvies:</strong> We brengen een beschermende zonnecrème (SPF) aan en adviseren u over de verzorging in de eerste dagen na de behandeling.</li>
                    </ol>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">HydraFacial zonder lange hersteltijd</h2>
                    <p>Een van de grootste troeven van HydraFacial is dat er vrijwel geen sprake is van een lange hersteltijd. De meeste cliënten kunnen hun normale werkzaamheden en sociale activiteiten direct na de sessie hervatten. Wel kan er een milde roodheid of lichte gevoeligheid optreden, passend bij de activering van de microcirculatie. Dit neemt meestal binnen enkele uren af. Individuele huidreacties kunnen variëren, en gedetailleerd nazorgadvies wordt altijd verstrekt.</p>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">HydraFacial combineren met huidverbetering</h2>
                    <p>HydraFacial kan goed passen binnen een breder en holistisch behandelingsplan voor langdurige gelaatsverjonging. Het vormt bijvoorbeeld de perfecte voorbereiding op intensievere therapieën:</p>
                    <ul className="list-disc pl-6 space-y-2 text-theme-muted">
                      <li><a href="/huidverbetering-antwerpen" className="text-primary underline hover:text-white transition-colors font-medium">Huidverbetering Antwerpen</a>: Een grondige huidanalyse en breder behandelplan om de algehele huidkwaliteit stapsgewijs te ondersteunen.</li>
                      <li><a href="/profhilo-antwerpen" className="text-primary underline hover:text-white transition-colors font-medium">Profhilo Antwerpen</a>: Te combineren om naast oppervlakkige glans ook de diepere elastine- en collageenaanmaak te stimuleren.</li>
                      <li><a href="/laserbehandeling-antwerpen" className="text-primary underline hover:text-white transition-colors font-medium">Laserbehandeling Antwerpen</a>: Ideaal om de huid eerst vrij te maken van dode cellen, zodat laserenergie nog egaler kan penetreren.</li>
                      <li><a href="/tarieven" className="text-primary underline hover:text-white transition-colors font-medium">Tarieven</a>: Ontdek onze transparante hydrafacial prijs Antwerpen en tarievenpagina.</li>
                    </ul>
                  </section>

                  <section className="space-y-4">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">HydraFacial in Antwerpen, Edegem en Hoboken</h2>
                    <p>Dr. An De Cuyper biedt deze effectieve dieptereiniging aan voor patiënten uit heel de Antwerpse regio. Of u nu woont in Antwerpen-Centrum, of behoefte heeft aan een veilige HydraFacial in Edegem (Clinic 2650) of regio Hoboken; u kunt overal rekenen op een professionele, uniforme en medisch hoogstaande zorg.</p>
                  </section>

                  <section className="space-y-6 pt-6 border-t border-white/10">
                    <h2 className="text-3xl font-display text-theme-text font-semibold">Veelgestelde Vragen (FAQ)</h2>
                    <div className="space-y-6">
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Is HydraFacial geschikt voor een gevoelige huid?</h4>
                        <p className="text-theme-muted">Ja, een HydraFacial-behandeling kan zo worden geconfigureerd dat deze ook geschikt is voor een gevoelige huid. De intensiteit en de gebruikte serums kunnen tijdens een persoonlijk consult worden afgestemd op uw specifieke barrièrefunctie.</p>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Hoe snel zie ik resultaat na een HydraFacial?</h4>
                        <p className="text-theme-muted">Vaak is er direct na de behandeling al een subtiele verbetering in hydratatie en glans merkbaar. De huid ziet er doorgaans frisser en schoner uit.</p>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Hoe vaak kan ik een HydraFacial laten uitvoeren?</h4>
                        <p className="text-theme-muted">Om de huidconditie optimaal te ondersteunen, kan een maandelijkse sessie worden geadviseerd. Dit bespreken we graag tijdens een persoonlijk adviesgesprek.</p>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Is er hersteltijd na HydraFacial?</h4>
                        <p className="text-theme-muted">Er is vrijwel geen sprake van een lange hersteltijd. Er kan een lichte, tijdelijke roodheid of milde gevoeligheid ontstaan, maar de meeste cliënten kunnen hun dagelijkse activiteiten direct hervatten.</p>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-2">Wat is het verschil tussen HydraFacial en huidverbetering?</h4>
                        <p className="text-theme-muted">HydraFacial is een specifieke 3-staps behandeling gericht op intensieve dieptereiniging, exfoliatie en hydratatie. Huidverbetering is een breder medisch-esthetisch concept waarbij verschillende therapieën (zoals peelings, skinboosters of lasers) in een behandelplan worden gecombineerd op basis van een diepgaande huidanalyse.</p>
                      </div>
                    </div>
                  </section>
                </div>
              }
            />
          } />

          <Route path="/esthetische-geneeskunde-antwerpen" element={
            <SEOPage 
              settings={data.settings}
              title="Esthetische Geneeskunde Antwerpen | Dr. An De Cuyper | Clinic 2650"
              h1="Esthetische Geneeskunde Antwerpen"
              description="Expert in esthetische geneeskunde te Antwerpen. Dr. An De Cuyper combineert medische precisie met natuurlijke esthetiek. Ontdek onze behandelingen."
              canonical="https://andecuyper.be/esthetische-geneeskunde-antwerpen"
              content={
                <>
                  <p>Welkom bij de praktijk van Dr. An De Cuyper, waar esthetische geneeskunde hand in hand gaat met medische integriteit. Gevestigd in de regio Antwerpen, bieden wij een veilige haven voor wie op zoek is naar verfijning.</p>
                  <p>Onze focus ligt op natuurlijke resultaten, uitgevoerd door een arts met jarenlange ervaring in de chirurgie. Wij begeleiden u graag in uw zoektocht naar een betere versie van uzelf.</p>
                </>
              }
            />
          } />

          {/* Practice Location Routes */}
          <Route path="/praktijk-antwerpen" element={
            <SEOPage 
              settings={data.settings}
              title="Praktijk Antwerpen | Esthetische Geneeskunde | Dr. An De Cuyper"
              h1="Praktijk Antwerpen"
              description="Onze hoofdlocatie in hartje Antwerpen. Gespecialiseerd in Botox, Fillers en Huidverbetering door Dr. An De Cuyper. Plan uw bezoek."
              canonical="https://andecuyper.be/praktijk-antwerpen"
              content={<p>Bezoek onze moderne kliniek in Antwerpen voor al uw esthetische behandelingen. Centraal gelegen en discreet.</p>}
            />
          } />
          <Route path="/praktijk-edegem" element={
            <SEOPage 
              settings={data.settings}
              title="Botox Edegem | Clinic 2650 | Dr. An De Cuyper"
              h1="Praktijk Edegem (Clinic 2650)"
              description="Op zoek naar de beste rimpelbehandeling of Botox in Edegem? Dr. An De Cuyper verwelkomt u bij Clinic 2650 (2650) voor professionele Botox en fillers."
              canonical="https://andecuyper.be/praktijk-edegem"
              content={
                <div className="space-y-4">
                  <p>In Edegem verwelkomen wij u bij Clinic 2650 voor hoogwaardige esthetische ingrepen in een vertrouwde omgeving.</p>
                  <p>Bent u specifiek geïnteresseerd in rimpelbehandelingen met botulinetoxine? Bezoek dan onze speciale pagina over <a href="/botox-edegem" className="text-primary underline hover:text-white transition-colors">Botox Edegem</a> voor uitgebreide informatie over onze werkwijze, tarieven en veelgestelde vragen.</p>
                </div>
              }
            />
          } />
          <Route path="/praktijk-hoboken" element={
            <SEOPage 
              settings={data.settings}
              title="Praktijk Hoboken | Esthetische Geneeskunde | Dr. An De Cuyper"
              h1="Praktijk Hoboken"
              description="Dr. An De Cuyper in Hoboken voor gespecialiseerde esthetische geneeskunde. Botox en rimpelbehandeling in uw regio."
              canonical="https://andecuyper.be/praktijk-hoboken"
              content={
                <div className="space-y-4">
                  <p>Cliënten uit Hoboken kunnen terecht in onze praktijk voor persoonlijke zorg en natuurlijke resultaten.</p>
                  <p>Bezoek onze gespecialiseerde pagina over <a href="/botox-hoboken" className="text-primary underline hover:text-white transition-colors">Botox Hoboken</a> voor gedetailleerde informatie over hoe wij rimpels op een veilige en medisch verantwoorde wijze verzachten.</p>
                </div>
              }
            />
          } />
          <Route path="/praktijk-burcht" element={
            <SEOPage 
              settings={data.settings}
              title="Praktijk Burcht | Esthetische Geneeskunde | Dr. An De Cuyper"
              h1="Praktijk Burcht"
              description="Esthetische consultaties en behandelingen in Burcht door Dr. An De Cuyper. Botox en fillers met medische precisie."
              canonical="https://andecuyper.be/praktijk-burcht"
              content={<p>Onze locatie in Burcht biedt een discrete setting voor uw esthetische consultaties en behandelingen.</p>}
            />
          } />

          <Route path="/contact" element={
            <SEOPage 
              settings={data.settings}
              title="Contact & Afspraak maken | Dr. An De Cuyper Antwerpen"
              h1="Contacteer Onze Praktijk"
              description="Neem contact op of plan direct uw consultatie in voor Botox, fillers of huidverbetering in Antwerpen, Edegem (Clinic 2650), Hoboken of Burcht."
              canonical="https://andecuyper.be/contact"
              content={
                <div className="space-y-6 text-theme-muted font-light leading-relaxed">
                  <p>Heeft u vragen over onze behandelingen of wenst u een persoonlijk esthetisch consult? Dr. An De Cuyper verwelkomt u graag in een van onze vlot bereikbare locaties in regio Antwerpen.</p>
                  <h2 className="text-2xl font-display text-theme-text mt-8 mb-4">Onze Contactgegevens</h2>
                  <p>U kunt ons direct telefonisch bereiken of e-mailen voor inlichtingen:</p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li><strong>Telefoon:</strong> <a href="tel:+3232844539" className="text-primary underline font-medium hover:text-white transition-colors">+32 3 284 45 39</a></li>
                    <li><strong>E-mail:</strong> <a href="mailto:info@drdecuyperan.be" className="text-primary underline font-medium hover:text-white transition-colors">info@drdecuyperan.be</a></li>
                    <li><strong>Openingsuren:</strong> Maandag t/m Vrijdag van 08:00 tot 20:15 uur</li>
                  </ul>
                  <h2 className="text-2xl font-display text-theme-text mt-8 mb-4">Praktijken in regio Antwerpen</h2>
                  <p>Dr. An De Cuyper is actief op de volgende consultatie-locaties:</p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li><strong>Praktijk Antwerpen:</strong> Binnenvaartstraat 95, 2000 Antwerpen</li>
                    <li><strong>Praktijk Edegem (Clinic 2650):</strong> Boniverlei 174 bus 8, 2650 Edegem</li>
                    <li><strong>Praktijk Hoboken:</strong> Hoboken, Antwerpen</li>
                    <li><strong>Praktijk Burcht:</strong> Burcht, regio Zwijndrecht</li>
                  </ul>
                </div>
              }
            />
          } />

          <Route path="/treatments" element={
            <SEOPage 
              settings={data.settings}
              title="Esthetische Behandelingen overzicht | Dr. An De Cuyper Antwerpen"
              h1="Esthetische Behandelingen Overzicht"
              description="Zorgvuldig overzicht van esthetische rimpelbehandelingen, lipfillers, draadjeslift, HydraFacial en huidverjonging te Antwerpen door Dr. An De Cuyper."
              canonical="https://andecuyper.be/treatments"
              content={
                <div className="space-y-6 text-theme-muted font-light leading-relaxed">
                  <p>Bij MD An De Cuyper bieden we uitsluitend onderbouwde esthetische behandelingen aan. Door onze medische en chirurgische achtergrond bent u verzekerd van maximale precisie en veiligheid.</p>
                  <h2 className="text-2xl font-display text-theme-text mt-8 mb-4">Ontdek onze behandelingen:</h2>
                  <ul className="list-disc pl-6 space-y-3 mt-2">
                    <li><a href="/botox-antwerpen" className="text-primary underline font-medium hover:text-white transition-colors">Botox Antwerpen</a> (of bekijk <a href="/botox-edegem" className="text-primary underline font-medium hover:text-white transition-colors">Botox Edegem</a> en <a href="/botox-hoboken" className="text-primary underline font-medium hover:text-white transition-colors">Botox Hoboken</a>): Effectieve behandeling van fronsrimpels, voorhoofdrimpels en kraaienpootjes.</li>
                    <li><a href="/fillers-antwerpen" className="text-primary underline font-medium hover:text-white transition-colors">Fillers Antwerpen</a>: Herstellen van natuurlijk volume in wangen, kin en contouren.</li>
                    <li><a href="/lippen-opvullen-antwerpen" className="text-primary underline font-medium hover:text-white transition-colors">Lippen Opvullen Antwerpen</a>: Subtiele en vollere lippen met veilige hyaluronzuur lipfillers.</li>
                    <li><a href="/profhilo-antwerpen" className="text-primary underline font-medium hover:text-white transition-colors">Profhilo Antwerpen</a>: Bioremodeling en intensieve huidverstrakking zonder extra volume.</li>
                    <li><a href="/draadjeslift-antwerpen" className="text-primary underline font-medium hover:text-white transition-colors">Draadjeslift Antwerpen (APTOS)</a>: Effectieve facelift zonder chirurgische incisies of operatie.</li>
                    <li><a href="/huidverbetering-antwerpen" className="text-primary underline font-medium hover:text-white transition-colors">Huidverbetering Antwerpen</a>: Medische skinboosters en peelings voor een herstelde huidtextuur.</li>
                    <li><a href="/hydrafacial-antwerpen" className="text-primary underline font-medium hover:text-white transition-colors">HydraFacial Antwerpen</a>: De nummer één dieptereiniging voor een onmiddellijke glow.</li>
                    <li><a href="/laserbehandeling-antwerpen" className="text-primary underline font-medium hover:text-white transition-colors">Laserbehandeling Antwerpen</a>: Fractional CO2 laser voor rimpels, littekens en grove poriën.</li>
                    <li><a href="/tarieven" className="text-primary underline font-medium hover:text-white transition-colors">Tarieven</a>: Transparant overzicht van onze prijzen en tarieven per behandeling.</li>
                  </ul>
                </div>
              }
            />
          } />

          {/* Fallback Legacy 1-hop Redirect Routes for client navigation */}
          <Route path="/home/" element={<Navigate to="/" replace />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/about" element={<Navigate to="/praktijk-antwerpen" replace />} />
          <Route path="/treatment/consult-1" element={<Navigate to="/contact" replace />} />
          <Route path="/treatment/aptos-1" element={<Navigate to="/draadjeslift-antwerpen" replace />} />
          <Route path="/treatment/aptos-3" element={<Navigate to="/draadjeslift-antwerpen" replace />} />
          <Route path="/treatment/aptos-6" element={<Navigate to="/draadjeslift-antwerpen" replace />} />
          <Route path="/treatment/cell-lumi" element={<Navigate to="/huidverbetering-antwerpen" replace />} />
          <Route path="/treatment/bio-booster" element={<Navigate to="/huidverbetering-antwerpen" replace />} />
          <Route path="/locations" element={<Navigate to="/praktijk-antwerpen" replace />} />
          
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiService.getInitialData();
      const auth = await apiService.checkAuth();
      setData(result);
      setIsAuthenticated(auth);
    } catch (error) {
      console.error("Error loading app:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    window.addEventListener('storage_sync', loadData);
    return () => window.removeEventListener('storage_sync', loadData);
  }, [loadData]);

  const handleLogin = () => setIsAuthenticated(true);

  if (loading && !data) {
    return (
      <div className="fixed inset-0 bg-[#0a0a0a] z-[9999] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <Router>
      <AppContent 
        data={data} 
        isAuthenticated={isAuthenticated}
        onUpdate={loadData} 
        onLogin={handleLogin}
      />
    </Router>
  );
};

export default App;
